import type { Action, Thunk } from 'easy-peasy';
import { action, thunk } from 'easy-peasy';
import type { StoreModel } from '..';
import type { ApplicationErrorOptions } from '../../types';
import type { Injections } from '../services/injections';
import type { Limit } from '../services/LimitService/types';
import type { PartnerLimitV2 } from '@shipium-corp/shipium-ts-types';
import parseStatusCode from '../errors/parseStatusCode';
import { modifyV1CostingLimitToV2, modifyV2CostingLimitToV1 } from './utils';
import type {
  PartnerCostAdjustConfig,
  PartnerCostAdjustConfigCreate,
  PartnerCostAdjustConfigPatch,
  PartnerCostAdjustConfigPut,
  PartnerSearchCostAdjustParams,
} from '@shipium-corp/shipium-ts-types';

export interface LimitModel {
  dynamicLimitActionStart: Action<LimitModel, void>;
  limitFailure: Action<LimitModel, ApplicationErrorOptions>;
  fetchPartnerCostingLimit: Thunk<LimitModel, void, Injections, StoreModel, Promise<Limit>>;
  createPartnerCostingLimit: Thunk<LimitModel, Limit, Injections, StoreModel, Promise<Limit>>;
  deleteLimit: Thunk<LimitModel, string, Injections, StoreModel, Promise<Limit>>;
  // right now there is only one of these allowed
  // everything in the page is base on this being 1 to 1
  // with partner
  partnerCostingLimit?: Limit;
  setPartnerCostingLimit: Action<LimitModel, Limit | undefined>;
  fetchCostAdjustConfigs: Thunk<
    LimitModel,
    PartnerSearchCostAdjustParams,
    Injections,
    Promise<PartnerCostAdjustConfig[]>
  >;
  fetchCostAdjustConfigById: Thunk<
    LimitModel,
    { costAdjustConfigId: string },
    Injections,
    Promise<PartnerCostAdjustConfig>
  >;
  costAdjustConfigs: Record<string, PartnerCostAdjustConfig>;
  setCostAdjustConfigs: Action<LimitModel, PartnerCostAdjustConfig[]>;
  createCostAdjustConfig: Thunk<
    LimitModel,
    PartnerCostAdjustConfigCreate,
    Injections,
    StoreModel,
    Promise<PartnerCostAdjustConfig>
  >;
  updateCostAdjustConfig: Thunk<
    LimitModel,
    { costAdjustConfigId: string } & PartnerCostAdjustConfigPatch,
    Injections,
    StoreModel,
    Promise<PartnerCostAdjustConfig>
  >;
  putCostAdjustConfig: Thunk<
    LimitModel,
    { costAdjustConfigId: string; costAdjustConfig: PartnerCostAdjustConfigPut },
    Injections,
    StoreModel,
    Promise<PartnerCostAdjustConfig>
  >;
}

export const limitModel: LimitModel = {
  costAdjustConfigs: {},
  // Body is a no-op; the action is dispatched solely to flip the dynamicLimit loading flag
  // via the listener in src/store/loading/index.ts.
  dynamicLimitActionStart: action(() => {}),
  limitFailure: action((state, error) => {
    console.error(error);
  }),
  fetchPartnerCostingLimit: thunk(async (actions, payload, { getStoreState, injections }) => {
    try {
      const storeState = getStoreState();
      let res;
      if (!storeState.partner.activePartner) {
        throw new Error('Active partner not found cannot determine version');
      }
      if (storeState.partner.activePartner?.featureToggles?.UI_ORTHO_V2 === 'true') {
        res = await injections.limitService.fetchV2Limits({
          includeArchive: false,
          originId: null,
          atDateTime: new Date().toISOString(),
        });
        res = res?.filter((l: PartnerLimitV2) => !l.originId);
        res = !!res && res.length > 0 ? [modifyV2CostingLimitToV1(res[0])] : [];
      } else {
        res = await injections.limitService.fetchPartnerCostingLimits();
      }
      if (res.length === 1) {
        if (res[0].active) {
          actions.setPartnerCostingLimit(res[0]);
        } else {
          return [];
        }
      } else if (res.length > 1) {
        // this should never hit unless limits are created from outside the ui
        throw new Error('Multiple Active Partner Costing limits');
      }
      return res;
    } catch (error) {
      actions.limitFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error occured, unable to fetch partner costing',
      });
      throw error;
    }
  }),
  createPartnerCostingLimit: thunk(async (actions, payload, { getStoreState, injections }) => {
    try {
      const storeState = getStoreState();
      let res;
      if (!storeState.partner.activePartner) {
        throw new Error('Active partner not found cannot determine version');
      }
      if (storeState.partner.activePartner?.featureToggles?.UI_ORTHO_V2 === 'true') {
        const v2LimCreate = modifyV1CostingLimitToV2(payload);
        res = await injections.limitService.createPartnerLimitV2(v2LimCreate);
        res = modifyV2CostingLimitToV1(res);
      } else {
        res = await injections.limitService.createPartnerCostingLimit(payload);
      }
      actions.setPartnerCostingLimit(res);
      return res;
    } catch (error) {
      actions.limitFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error occured, unable to create partner costing limit',
        asNotification: true,
      });
      throw error;
    }
  }),
  deleteLimit: thunk(async (actions, payload, { getStoreState, injections }) => {
    const storeState = getStoreState();
    let res;
    try {
      if (!storeState.partner.activePartner) {
        throw new Error('Active partner not found cannot determine version');
      }
      if (storeState.partner.activePartner?.featureToggles?.UI_ORTHO_V2 === 'true') {
        res = await injections.limitService.updatePartnerLimitV2(payload, { active: false, archive: true } as any);
      } else {
        res = await injections.limitService.deleteLimit(payload);
      }
      actions.setPartnerCostingLimit(undefined);
      return res;
    } catch (error) {
      actions.limitFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'Failed to delete limit',
        asNotification: true,
      });
      throw error;
    }
  }),
  setPartnerCostingLimit: action((state, payload) => {
    state.partnerCostingLimit = payload;
  }),
  // cost adjust configs, aka dynamic limits
  setCostAdjustConfigs: action((state, payload) => {
    state.costAdjustConfigs = payload.reduce(
      (prev: Record<string, PartnerCostAdjustConfig>, val) => {
        prev[val.costAdjustConfigId] = val;
        return prev;
      },
      { ...state.costAdjustConfigs },
    );
  }),
  fetchCostAdjustConfigs: thunk(async (actions, payload, { injections }) => {
    try {
      actions.dynamicLimitActionStart();
      const res = await injections.limitService.fetchCostAdjustConfigs(payload);

      // filter archived, sort by active, then date
      res
        ?.filter((x) => !x.archived)
        .sort(
          (a, b) =>
            +b.active - +a.active ||
            (b?.auditInfo?.lastUpdatedTimeStamp || 'z').localeCompare(a?.auditInfo?.lastUpdatedTimeStamp || 'z'),
        );

      actions.setCostAdjustConfigs(res);
      return res;
    } catch (error) {
      actions.limitFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'Unable to fetch dynamic limits',
        asNotification: true,
      });
      throw error;
    }
  }),
  fetchCostAdjustConfigById: thunk(async (actions, payload, { injections }) => {
    try {
      actions.dynamicLimitActionStart();
      const res = await injections.limitService.fetchCostAdjustConfig(payload.costAdjustConfigId);
      actions.setCostAdjustConfigs([res]);
      return res;
    } catch (error) {
      actions.limitFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'Unable to fetch dynamic limits',
        asNotification: true,
      });
      throw error;
    }
  }),
  createCostAdjustConfig: thunk(async (actions, payload, { injections }) => {
    try {
      actions.dynamicLimitActionStart();
      const res = await injections.limitService.createCostAdjustConfig(payload);
      actions.setCostAdjustConfigs([res]);
      return res;
    } catch (error) {
      actions.limitFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'Unable to create dynamic limit',
        asNotification: true,
      });
      throw error;
    }
  }),
  updateCostAdjustConfig: thunk(async (actions, payload, { injections }) => {
    try {
      actions.dynamicLimitActionStart();
      const { costAdjustConfigId, ...body } = payload;
      const res = await injections.limitService.updateCostAdjustConfig(costAdjustConfigId, body);
      actions.setCostAdjustConfigs([res]);
      return res;
    } catch (error) {
      actions.limitFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'Unable to update dynamic limit',
        asNotification: true,
      });
      throw error;
    }
  }),
  putCostAdjustConfig: thunk(async (actions, payload, { injections }) => {
    try {
      actions.dynamicLimitActionStart();
      const { costAdjustConfigId, costAdjustConfig } = payload;
      const res = await injections.limitService.putCostAdjustConfig(costAdjustConfigId, costAdjustConfig);
      actions.setCostAdjustConfigs([res]);
      return res;
    } catch (error) {
      actions.limitFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'Unable to update dynamic limit',
        asNotification: true,
      });
      throw error;
    }
  }),
};
